import { useState, useEffect } from "react";
import { getUsers } from "../services/userService";

interface ApiError {
  message?: string;
}

type NormalizedRoleLabel =
  | "Admin"
  | "Inspector"
  | "Owner"
  | "Lender"
  | "GC"
  | "Architect"
  | "";

const ROLE_LABEL_MAP: Record<string, NormalizedRoleLabel> = {
  admin: "Admin",
  inspector: "Inspector",
  owner: "Owner",
  bank: "Lender",
  lender: "Lender",
  gc: "GC",
  architect: "Architect",
};

const STATUS_MAP: Record<string, string> = {
  active: "Active",
  pending_invite: "Pending",
  inactive: "Inactive",
};

const COLOR_MAP: Record<string, string> = {
  Admin: "#5b21b6",
  Inspector: "#14508c",
  Owner: "#1a6b35",
  Lender: "#7a5c00",
  GC: "#0f6b6b",
  Architect: "#a32d2d",
};

export interface RawUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: string;
  companyName?: string;
  bio?: string;
  roles?: string[];
  status?: string;
  lenderBranch?: { branchName?: string; companyName?: string; id?: string; bankId?: string };
  assignedProjects?: { id: string }[];
  inspectorMeta?: { designation?: string | null; focused?: string[] | null } | null;
  lastLoginAt?: string | null;
  avatar?: { url?: string; key?: string; fileName?: string } | null;
}

export interface NormalizedUser {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  company: string;
  bio: string;
  role: NormalizedRoleLabel;
  roles: NormalizedRoleLabel[];
  bank: string;
  bankId: string;
  bankName: string;
  branchId: string;
  status: string;
  projects: string[];
  initials: string;
  color: string;
  lastLoginAt: string | null;
  inspectorMeta: { designation: string; focused: string[] };
  avatar: string | null;
}

export interface UserPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const normalizeUser = (u: RawUser): NormalizedUser => {
  const role = ROLE_LABEL_MAP[u.roles?.[0] ?? ""] ?? "";
  const roles = (u.roles ?? [])
    .map((r) => ROLE_LABEL_MAP[r])
    .filter((r): r is NormalizedRoleLabel => !!r);
  const initials =
    `${u.firstName?.charAt(0) ?? ""}${u.lastName?.charAt(0) ?? ""}`.toUpperCase();

  return {
    id: u.id,
    name: `${u.firstName?.charAt(0) ?? ""}. ${u.lastName ?? ""}`,
    firstName: u.firstName ?? "",
    lastName: u.lastName ?? "",
    email: u.email ?? "",
    phone: u.phone ?? "",
    address: u.address ?? "",
    company: u.companyName ?? "",
    bio: u.bio ?? "",
    role,
    roles,
    bank: u.lenderBranch?.branchName ?? "",
    bankId: u.lenderBranch?.bankId ?? "",
    bankName: u.lenderBranch?.companyName ?? "",
    branchId: u.lenderBranch?.id ?? "",
    status: STATUS_MAP[u.status ?? ""] ?? u.status ?? "",
    // A project is listed once per role the user holds on it; show each project only once.
    projects: [...new Set((u.assignedProjects ?? []).map((p) => p.id))],
    initials,
    color: COLOR_MAP[role] ?? "#14508c",
    lastLoginAt: u.lastLoginAt ?? null,
    avatar: u.avatar?.url ?? null,
    inspectorMeta: {
      designation: (u.inspectorMeta?.designation ?? "").toLowerCase(),
      focused: (u.inspectorMeta?.focused ?? []).map((v) => v.toLowerCase()),
    },
  };
};

const useGetUsers = (
  page = 1,
  limit = 10,
  filter = "",
  status = "",
  search = "",
) => {
  const [users, setUsers] = useState<NormalizedUser[]>([]);
  const [pagination, setPagination] = useState<UserPagination>({
    total: 0,
    page: 1,
    limit,
    totalPages: 1,
  });
  const [stats, setStats] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refetchKey, setRefetchKey] = useState(0);
  const refetch = () => setRefetchKey((k) => k + 1);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError("");

      const params: Record<string, unknown> = { page, limit };
      if (filter && filter !== "all") params.role = filter;
      if (status && status !== "all") params.status = status;
      if (search.trim()) params.q = search.trim();

      const { data, error: apiError } = (await getUsers(params)) as {
        data: {
          data?: RawUser[];
          pagination?: UserPagination;
          addOns?: { stats?: Record<string, unknown> };
        } | null;
        error: ApiError | null;
      };

      if (apiError) {
        setError(apiError.message || "Failed to load users.");
        setLoading(false);
        return;
      }

      setUsers((data?.data ?? []).map(normalizeUser));
      setPagination(
        data?.pagination ?? { total: 0, page, limit, totalPages: 1 },
      );
      setStats(data?.addOns?.stats ?? null);
      setLoading(false);
    };

    fetchUsers();
  }, [page, limit, filter, status, search, refetchKey]);

  return { users, setUsers, pagination, stats, loading, error, refetch };
};

export default useGetUsers;
